import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Demo credentials are intentionally rendered only when the build is in
// "showcase / staging" mode (controlled by REACT_APP_SHOW_DEMO_CREDENTIALS).
// In production this flag must be set to "false" so the cards are not
// included in the bundle.
const SHOW_DEMO = (process.env.REACT_APP_SHOW_DEMO_CREDENTIALS || '').toLowerCase() === 'true';

// These are seeded demo accounts only — they map to throwaway passwords created
// by /app/backend/seed.py. They are NOT production credentials and never grant
// access to any external system.
const DEMO_ACCOUNTS = SHOW_DEMO ? [
  { label: 'Super Admin', email: 'admin@algti.com', password: 'Admin@123' },
  { label: 'Admin', email: 'admin@demo.com', password: 'Admin@123' },
  { label: 'Supervisor', email: 'supervisor@demo.com', password: 'Super@123' },
  { label: 'Atendente', email: 'atendente@demo.com', password: 'Atend@123' },
  { label: 'Cliente', email: 'cliente@demo.com', password: 'Client@123' },
] : [];

export default function LoginPage() {
  const { login } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login realizado com sucesso!');
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Falha ao entrar');
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (d) => {
    setEmail(d.email);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50">
      <div 
        className="hidden lg:flex flex-1 text-white p-10 relative overflow-hidden"
        style={{
          background: `linear-gradient(to bottom right, ${theme.primaryColor}, ${theme.secondaryColor}, ${theme.accentColor})`
        }}
      >
        <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-white/20 blur-3xl" />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-12">
            {theme.logoUrl ? (
              <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur overflow-hidden flex items-center justify-center p-1">
                <img 
                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${theme.logoUrl}`} 
                  alt={theme.companyName}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
                <ShieldCheck className="w-6 h-6" />
              </div>
            )}
            <div className="text-2xl font-semibold tracking-tight">{theme.companyName}</div>
          </div>
          <h1 className="text-4xl font-bold leading-tight mb-4">{theme.loginTitle}</h1>
          {theme.loginSubtitle && (
            <p className="text-blue-100 text-lg mb-4">{theme.loginSubtitle}</p>
          )}
          <p className="text-blue-100 text-lg">
            Centralize chamados de garantia, automatize fluxos e mantenha o controle total do seu pós-venda.
          </p>
          <ul className="mt-8 space-y-3 text-blue-50">
            <li className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-white mt-2" />SLA visual e alertas automáticos</li>
            <li className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-white mt-2" />Multi-tenant com isolamento total</li>
            <li className="flex gap-3"><span className="w-1.5 h-1.5 rounded-full bg-white mt-2" />Histórico completo e auditoria de ações</li>
          </ul>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            {theme.logoUrl ? (
              <div className="w-10 h-10 rounded-xl overflow-hidden bg-white flex items-center justify-center p-1 border border-slate-200">
                <img 
                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}${theme.logoUrl}`} 
                  alt={theme.companyName}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: theme.primaryColor }}>
                <ShieldCheck className="w-5 h-5" />
              </div>
            )}
            <div className="text-xl font-semibold">{theme.companyName}</div>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900">Bem-vindo de volta</h2>
          <p className="text-sm text-slate-500 mt-1">Entre com sua conta para continuar.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" autoComplete="on">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" data-testid="login-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input id="password" data-testid="login-password" type={show ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                <button type="button" aria-label={show ? 'Ocultar senha' : 'Mostrar senha'} onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 text-slate-600">
                <Checkbox /> Lembrar-me
              </label>
              <button type="button" className="text-blue-600 hover:underline">Esqueci a senha</button>
            </div>
            <Button data-testid="login-submit" type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando...</> : 'Entrar'}
            </Button>
          </form>

          {SHOW_DEMO && DEMO_ACCOUNTS.length > 0 && (
            <div className="mt-8 border-t pt-5">
              <div className="text-xs text-slate-500 mb-2">Contas de demonstração (somente em ambiente de homologação):</div>
              <div className="grid grid-cols-2 gap-2">
                {DEMO_ACCOUNTS.map((d) => (
                  <button key={d.email} type="button" onClick={() => fillDemo(d)} className="text-left text-xs px-3 py-2 rounded-lg border border-slate-200 hover:border-blue-500 hover:bg-blue-50 transition">
                    <div className="font-medium text-slate-800">{d.label}</div>
                    <div className="text-slate-500 truncate">{d.email}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
